import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatErr } from '@/lib/api';
import { BRAND } from '@/lib/brand';
import { ArrowRight, ShieldCheck, Users, PhoneCall } from 'lucide-react';

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      nav('/');
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden md:flex flex-col justify-between p-10 bg-[hsl(var(--sidebar-bg))] text-white overflow-hidden grain">
        <div className="relative z-10 flex items-center gap-3">
          <img src={BRAND.logoUrl} alt={BRAND.short} className="w-11 h-11 rounded-md bg-white p-1 object-contain" />
          <div>
            <div className="font-display text-xl font-black tracking-tight leading-none">{BRAND.short}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{BRAND.full}</div>
          </div>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          <div className="text-xs tracking-[0.3em] text-zinc-500 uppercase">{BRAND.tagline}</div>
          <h1 className="font-display text-5xl font-black leading-[1.02] tracking-tight">
            Placing India's <span className="text-[#EF4444]">best</span> talent — <span className="text-[#3B82F6]">faster.</span>
          </h1>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Sarika HR Solutions is a Delhi-NCR based recruitment agency helping high-growth companies hire outstanding people across tech, product, sales & operations. This is our internal command centre.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-zinc-800">
            <div>
              <PhoneCall className="w-4 h-4 text-[#EF4444] mb-1" />
              <div className="font-display text-2xl font-black">9+</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Job Portals</div>
            </div>
            <div>
              <Users className="w-4 h-4 text-[#3B82F6] mb-1" />
              <div className="font-display text-2xl font-black">Full team</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Visibility</div>
            </div>
            <div>
              <ShieldCheck className="w-4 h-4 text-emerald-400 mb-1" />
              <div className="font-display text-2xl font-black">24/7</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Punch tracking</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-zinc-500">© {new Date().getFullYear()} {BRAND.full}</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8 md:hidden">
            <img src={BRAND.logoUrl} alt={BRAND.short} className="w-10 h-10 rounded-md bg-white p-1 object-contain" />
            <div>
              <div className="font-display text-xl font-black">{BRAND.short}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{BRAND.full}</div>
            </div>
          </div>
          <div className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-3">Sign in</div>
          <h2 className="font-display text-4xl font-black tracking-tight mb-2">Welcome back</h2>
          <p className="text-sm text-muted-foreground mb-8">Enter your credentials to access the {BRAND.short} command centre.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Work email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11 bg-primary hover:bg-primary/90 text-white font-semibold group">
              {busy ? 'Signing in…' : (<>Sign in <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-0.5" /></>)}
            </Button>
          </form>

          <div className="mt-8 border-t border-border/70 pt-6 text-xs text-muted-foreground">
            Need an account? Ask your admin to add you from the Team panel.
          </div>
        </div>
      </div>
    </div>
  );
}
